import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import moment from "moment";

// SuperAdmin-only display preference: every date/time the SuperAdmin UI shows
// comes back from the API as a UTC instant (standard Mongo behavior), and up
// to now every screen rendered it in whatever timezone the SuperAdmin's own
// browser/OS happens to be set to via moment()/toLocaleString() with no
// explicit zone. That's fine for a store's own staff (their POS device's
// local time IS the store's time), but a SuperAdmin can be anywhere - this
// lets them pick one fixed zone so every timestamp in the SuperAdmin shell
// reads consistently regardless of where they're physically logging in
// from. Starts on whatever this device's own system timezone actually is
// (see resolveClosestOption/detectSystemTimezone below) until they
// explicitly pick a different one, which then persists. Deliberately
// scoped: the Provider is only mounted around the SuperAdmin branch in
// App.js, and useTimezone() is safe to call from a component shared with
// regular store pages (ProgramLogs.js) - it just falls back to unchanged,
// local-browser-time formatting there.
const STORAGE_KEY = "superAdminTimezone";
// Last-resort fallback only (detection failed entirely) - no longer the
// everyday default now that the dropdown auto-selects the device's own
// system zone instead.
export const DEFAULT_TIMEZONE = "Asia/Kolkata";

// A fixed list (not Intl.supportedValuesOf("timeZone"), which isn't
// available in every supported browser) covering every populated UTC
// offset, each an IANA zone id so DST is handled correctly for that
// specific city rather than a bare fixed offset. `country` is the
// representative nation shown as a flag; `search` is extra text (older/
// alternate names) the dropdown's search box also matches against.
export const TIMEZONE_OPTIONS = [
  { value: "Pacific/Midway", city: "Midway, Samoa", country: "WS", offset: "UTC-11:00" },
  { value: "Pacific/Honolulu", city: "Hawaii", country: "US", offset: "UTC-10:00", search: "Honolulu" },
  { value: "America/Anchorage", city: "Alaska", country: "US", offset: "UTC-09:00", search: "Anchorage" },
  { value: "America/Los_Angeles", city: "Los Angeles, Vancouver", country: "US", offset: "UTC-08:00", search: "Seattle California" },
  { value: "America/Denver", city: "Denver, Phoenix", country: "US", offset: "UTC-07:00" },
  { value: "America/Chicago", city: "Chicago, Mexico City", country: "US", offset: "UTC-06:00" },
  { value: "America/New_York", city: "New York, Toronto", country: "US", offset: "UTC-05:00" },
  { value: "America/Halifax", city: "Halifax", country: "CA", offset: "UTC-04:00" },
  { value: "America/Sao_Paulo", city: "Sao Paulo, Buenos Aires", country: "BR", offset: "UTC-03:00" },
  { value: "Atlantic/Azores", city: "Azores", country: "PT", offset: "UTC-01:00" },
  { value: "Europe/London", city: "London, Dublin, Lisbon", country: "GB", offset: "UTC+00:00" },
  { value: "Europe/Paris", city: "Paris, Berlin, Madrid, Lagos", country: "FR", offset: "UTC+01:00" },
  { value: "Europe/Athens", city: "Athens, Cairo, Helsinki", country: "GR", offset: "UTC+02:00" },
  { value: "Europe/Moscow", city: "Moscow, Istanbul, Nairobi", country: "RU", offset: "UTC+03:00" },
  { value: "Asia/Dubai", city: "Dubai, Abu Dhabi", country: "AE", offset: "UTC+04:00" },
  { value: "Asia/Karachi", city: "Karachi, Islamabad", country: "PK", offset: "UTC+05:00" },
  { value: "Asia/Kolkata", city: "Mumbai, Delhi, Bengaluru", country: "IN", offset: "UTC+05:30", search: "India IST" },
  { value: "Asia/Kathmandu", city: "Kathmandu", country: "NP", offset: "UTC+05:45" },
  { value: "Asia/Dhaka", city: "Dhaka", country: "BD", offset: "UTC+06:00" },
  { value: "Asia/Bangkok", city: "Bangkok, Jakarta", country: "TH", offset: "UTC+07:00" },
  { value: "Asia/Shanghai", city: "Beijing, Singapore, Hong Kong", country: "CN", offset: "UTC+08:00" },
  { value: "Asia/Tokyo", city: "Tokyo, Seoul, Osaka", country: "JP", offset: "UTC+09:00" },
  { value: "Australia/Sydney", city: "Sydney, Melbourne", country: "AU", offset: "UTC+10:00" },
  { value: "Pacific/Auckland", city: "Auckland, Fiji", country: "NZ", offset: "UTC+12:00" },
];

// Returns a Date whose LOCAL getters (what moment()/toLocaleString() without
// an explicit zone read from) reflect the wall-clock time in `timeZone` -
// i.e. it shifts the instant, not just re-labels it, so the browser's own
// timezone stops mattering once this runs. Not exported - formatInTimezone
// below is the only supported way to use it, so a UTC offset ever gets
// applied consistently instead of a caller reading raw getters off this and
// getting a value that looks local but isn't.
const toZonedDate = (date, timeZone) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return d;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const map = {};
  parts.forEach(({ type, value }) => {
    map[type] = value;
  });
  // Intl reports midnight as "24" in hour12:false for some locales/engines -
  // normalize so `new Date(...)` doesn't roll over to the wrong day.
  const hour = map.hour === "24" ? 0 : Number(map.hour);
  return new Date(Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), Number(map.second));
};

// Pure function, usable outside React too (not just via the hook) - a plain
// module-level formatter (e.g. ProgramLogConstants.js's formatDateTime) can
// import this directly. `timeZone` falsy (undefined/null) is the existing,
// unchanged behavior: format in whatever zone the browser is already in.
export const formatInTimezone = (date, formatStr = "MMM DD, YYYY hh:mm A", timeZone) => {
  if (!date) return "-";
  if (!timeZone) return moment(date).format(formatStr);
  return moment(toZonedDate(date, timeZone)).format(formatStr);
};

// This device's own OS/browser timezone - shown publicly in the regular
// Footer.js for every user (not gated to SuperAdmin), and used below to
// pick the SuperAdmin dropdown's starting value before any explicit choice
// has been saved.
export const detectSystemTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch (error) {
    return DEFAULT_TIMEZONE;
  }
};

// Current UTC offset of `timeZone`, in minutes - computed live (not parsed
// from TIMEZONE_OPTIONS' static "offset" label strings) so it's correct
// year-round across DST transitions.
const getOffsetMinutes = (timeZone, date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })
    .formatToParts(date)
    .reduce((acc, { type, value }) => {
      acc[type] = value;
      return acc;
    }, {});
  const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return Math.round((asUTC - date.getTime()) / 60000);
};

// Looks up a TIMEZONE_OPTIONS entry by IANA id, for display (flag/city/
// offset) purposes.
export const getTimezoneOption = (value) => TIMEZONE_OPTIONS.find((opt) => opt.value === value) || null;

// Last path segment of an IANA id ("Asia/Kolkata" -> "Kolkata") - used where
// a curated-list lookup isn't appropriate (the public Footer.js shows
// whatever zone the device is actually in, not a snapped-to-nearest-option
// approximation).
export const shortZoneCity = (timeZone) => (timeZone || "").split("/").pop().replace(/_/g, " ");

// "GMT+5:30" (or a recognized abbreviation like "PST" where the JS engine's
// ICU data has one) for `timeZone` right now - computed live via Intl, so
// it's correct through DST changes without needing any lookup table.
export const getZoneOffsetLabel = (timeZone, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value || timeZone;
  } catch (error) {
    return timeZone;
  }
};

// TIMEZONE_OPTIONS is a curated ~24-entry list (one representative city per
// populated UTC offset band), not every one of the tz database's ~400
// zones - so a device reporting e.g. "America/Toronto" (real IANA zone,
// but sharing New York's offset/DST rules, which is the entry actually in
// the list) needs a fallback beyond an exact string match. Falls back to
// whichever curated option currently shares the same UTC offset, so the
// dropdown's default selection is never a wrong-looking mismatch (like
// showing the Midway/Samoa flag for a US Eastern-time device).
export const resolveClosestOption = (ianaZone) => {
  const exact = getTimezoneOption(ianaZone);
  if (exact) return exact;
  try {
    const targetOffset = getOffsetMinutes(ianaZone);
    return TIMEZONE_OPTIONS.reduce((best, opt) => {
      const diff = Math.abs(getOffsetMinutes(opt.value) - targetOffset);
      return diff < best.diff ? { diff, opt } : best;
    }, { diff: Infinity, opt: TIMEZONE_OPTIONS[0] }).opt;
  } catch (error) {
    return getTimezoneOption(DEFAULT_TIMEZONE) || TIMEZONE_OPTIONS[0];
  }
};

const TimezoneContext = createContext(null);

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezoneState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return saved;
    } catch (error) {
      // Private-browsing/storage-blocked - fall through to auto-detection.
    }
    // No explicit choice saved yet - default to whichever curated option
    // matches this device's own OS/browser timezone (see
    // detectSystemTimezone/resolveClosestOption above), rather than always
    // starting on India regardless of where the SuperAdmin actually is.
    return resolveClosestOption(detectSystemTimezone()).value;
  });

  const setTimezone = useCallback((tz) => {
    setTimezoneState(tz);
    try {
      localStorage.setItem(STORAGE_KEY, tz);
    } catch (error) {
      // Private-browsing/storage-blocked - the selection just won't survive
      // a refresh, which is a minor inconvenience, not worth surfacing.
    }
  }, []);

  const format = useCallback((date, formatStr) => formatInTimezone(date, formatStr, timezone), [timezone]);

  const value = useMemo(
    () => ({ timezone, setTimezone, formatInTimezone: format }),
    [timezone, setTimezone, format]
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

// Safe to call from anywhere, including components shared with regular
// store pages: outside the Provider (i.e. not inside the SuperAdmin shell)
// `timezone` comes back null and formatInTimezone behaves exactly like a
// bare moment(date).format(formatStr) call always has.
export const useTimezone = () => {
  const ctx = useContext(TimezoneContext);
  if (ctx) return ctx;
  return {
    timezone: null,
    setTimezone: () => {},
    formatInTimezone: (date, formatStr) => formatInTimezone(date, formatStr, null),
  };
};
